import dayjs from "dayjs";
import React, {
  useMemo,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { api, handleErrors } from "helpers/api";
import useGet from "hooks/useGet";
import { notification } from "antd";

const CalendarContext = React.createContext();
let timer;

function CalendarIssuesProvider({
  children,
  origin_source,
  worker_id: default_worker_id,
  view_type: default_view_type = "week",
  issue_id,
  date,
  handleClose,
  handleConfirm,
  onWorkerChange,
  onDateChange,
  onViewTypeChange,
}) {
  const [calendar_date, setCalendarDate] = useState(date);
  const [calendar_issue_id, setCalendarIssueId] = useState(issue_id);
  const [view_type, setViewType] = useState(default_view_type);
  const [is_scrolled, setScrolled] = useState(false);
  const [show_night_hours, setShowNightHours] = useState(false);
  const [draggable_event, setDraggableEvent] = useState(null);
  const [is_open_aside_panel, setOpenAsidePanel] = useState(false);
  const [worker_id, setWorkerId] = useState(default_worker_id);

  const is_editable = !calendar_issue_id;

  const onConfirm = useCallback(
    (date) => handleConfirm?.(date),
    [handleConfirm]
  );

  const { is_fetching: is_loading_calendar_issue, data: calendar_issue } =
    useGet({
      query: ["calendar_issue", calendar_issue_id],
      path: `/issues/${calendar_issue_id}`,
      context: "calendar_day_details",
      options: {
        enabled: !!calendar_issue_id,
        refetchOnWindowFocus: false,
      },
    });

  const view_type_period = useMemo(() => {
    if (view_type === "day") {
      return "date";
    }
    if (view_type === "week") {
      return "week";
    }
    return "month";
  }, [view_type]);

  const {
    is_loading: is_loading_tasks,
    data: tasks = [],
    refetch: refetchTasks,
  } = useGet({
    query: ["tasks", calendar_date, view_type],
    path: "/tasks",
    context: view_type === "month" ? "calendar_month" : "calendar_day",
    search: {
      per_page: 200,
      "q[date_gteq]": dayjs(calendar_date)
        .startOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
      "q[date_lteq]": dayjs(calendar_date)
        .endOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
    },
    options: {
      enabled: !!calendar_date,
      refetchOnWindowFocus: false,
    },
  });

  const {
    is_loading: is_loading_events,
    data: events = [],
    refetch: refetchEvents,
  } = useGet({
    query: ["events", calendar_date, worker_id, view_type],
    path: "/issues",
    context: view_type === "month" ? "calendar_month" : "calendar_day",
    search: {
      date: calendar_date,
      per_page: 200,
      sort: "service_date__desc",
      ...(worker_id ? { "q[worker_id_eq]": worker_id } : {}),
      "q[service_date_gteq]": dayjs(calendar_date)
        .startOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
      "q[service_date_lteq]": dayjs(calendar_date)
        .endOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
    },
    options: {
      enabled: !!calendar_date,
      refetchOnWindowFocus: false,
    },
  });

  const { data: holidays = [] } = useGet({
    query: ["holidays", calendar_date, view_type],
    path: "/holidays",
    search: {
      date_start: dayjs(calendar_date)
        .startOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
      date_end: dayjs(calendar_date)
        .endOf(view_type_period)
        .format("DD-MM-YYYY HH:mm"),
    },
    options: {
      enabled: !!calendar_date,
      refetchOnWindowFocus: false,
    },
  });

  const onCancel = () => {
    setScrolled(false);
    handleClose();
  };

  const handleConfirmDragIssue = async (issue_id) => {
    try {
      await api.put(`/issues/${issue_id}/set_service_date`, {
        service_date: dayjs(draggable_event.date).format(),
      });
      notification.success({
        message: "Zaktualizowano date umówienia",
      });
      refetchEvents();
      setDraggableEvent(null);
    } catch (error) {
      notification.error({ message: handleErrors(error) });
    }
  };

  const confirmMoveIssue = useCallback(
    async (date) => {
      try {
        await api.put(`/issues/${calendar_issue?.id}/set_service_date`, {
          service_date: dayjs(date).format(),
        });
        notification.success({
          message: `Zaktualizowano date umówienia dla zgłoszenia ${calendar_issue.identifier}`,
        });
        refetchEvents();
        setCalendarIssueId(null);
      } catch (error) {
        notification.error({ message: handleErrors(error) });
      }
      onConfirm(date);
    },
    [calendar_issue, refetchEvents, onConfirm]
  );

  const handleConfirmDragTask = async (task_id) => {
    try {
      await api.post(`/tasks/${task_id}/move_to_date`, {
        date: dayjs(draggable_event.date).format(),
      });
      notification.success({
        message: "Przeniesiono pomyślnie",
      });
      refetchTasks();
      setDraggableEvent(null);
    } catch (error) {
      notification.error({ message: handleErrors(error) });
    }
  };

  useEffect(() => {
    if (events?.length > 0 && !is_scrolled && view_type === "day") {
      const has_night_events = events.some(({ service_date }) => {
        const hour = dayjs(service_date).hour();
        return hour < 6 || hour >= 22;
      });

      setShowNightHours(has_night_events);

      // 🔽 automatyczne przewinięcie do pierwszego eventu
      const firstEvent = [...events].sort(
        (a, b) => new Date(a.service_date) - new Date(b.service_date)
      )[0];

      timer = setTimeout(() => {
        const container = document.querySelector(
          ".ant-modal-root .ant-modal-wrap.ant-modal-centered"
        );
        const el = document.getElementById(firstEvent.id);

        if (container && el) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = el.getBoundingClientRect().top;

          const scrollOffset =
            elementTop - containerTop + container.scrollTop - 100;

          container.scrollTo({
            top: scrollOffset,
            behavior: "smooth",
          });
          setScrolled(true);
        }
      }, 500);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [events]);

  useEffect(() => {
    onWorkerChange?.(worker_id);
    // eslint-disable-next-line
  }, [worker_id]);

  useEffect(() => {
    onDateChange?.(dayjs(calendar_date).toISOString());
    // eslint-disable-next-line
  }, [calendar_date]);

  useEffect(() => {
    onViewTypeChange?.(view_type);
    // eslint-disable-next-line
  }, [view_type]);

  const value = useMemo(() => {
    return {
      worker_id,
      is_editable,
      view_type,
      calendar_date,
      is_loading_tasks,
      is_loading_events,
      is_loading_calendar_issue,
      is_open_aside_panel,
      calendar_issue,
      show_night_hours,
      tasks,
      events,
      holidays,
      draggable_event,
      origin_source,
      refetchTasks,
      refetchEvents,
      setCalendarDate,
      setViewType,
      onCancel,
      onConfirm,
      setShowNightHours,
      setDraggableEvent,
      handleConfirmDragIssue,
      handleConfirmDragTask,
      setCalendarIssueId,
      confirmMoveIssue,
      setOpenAsidePanel,
      setWorkerId,
    };
    // eslint-disable-next-line
  }, [
    is_open_aside_panel,
    is_loading_calendar_issue,
    calendar_issue,
    is_editable,
    view_type,
    calendar_date,
    draggable_event,
    is_loading_tasks,
    is_loading_events,
    tasks,
    events,
    holidays,
    show_night_hours,
    worker_id,
    origin_source,
  ]);
  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

const useCalendarIssues = () => useContext(CalendarContext);
export { useCalendarIssues };
export default CalendarIssuesProvider;
